<template>
  <div
    class="carvia-container fixed inset-x-0 bottom-4 !my-0 rounded-lg bg-white !py-4 shadow-lg transition-transform"
    :class="{ 'translate-y-[20rem]': !visibleJobs.length }"
  >
    <TransitionGroup
      tag="div"
      class="space-y-2"
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform translate-y-full opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform translate-y-full opacity-0"
    >
      <div v-for="job in visibleJobs" :key="job.id">
        <div class="flex items-center gap-2">
          <Spinner v-if="job.status === 'active'" class="h-4 w-4 animate-spin text-primary" />
          <HourglassIcon
            v-else-if="['waiting', 'delayed'].includes(job.status)"
            class="animate-hourglass h-4 w-4 text-obsidian-500"
          />
          <AlertCircleIcon
            v-else-if="job.status === 'failed'"
            class="h-4 w-4 text-bittersweet-600"
          />
          <span class="text-sm font-medium">{{ t(`jobTypes.${job.name}`) }}</span>

          <span v-if="job.step" class="text-sm text-obsidian-500">
            - {{ t(`jobSteps.${job.step}`) }} </span
          ><span
            v-if="job.delayReason && job.status === 'delayed'"
            class="text-sm text-obsidian-500"
          >
            ({{ t(`delayReasons.${job.delayReason}`) }})
          </span>
          <span v-else-if="relativeRunAt(job)" class="text-sm text-obsidian-500">
            ({{ relativeRunAt(job) }})
          </span>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import { AlertCircleIcon, HourglassIcon } from 'lucide-vue-next';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { useBookingJobs } from '@/queries/use-booking-jobs.ts';
import { useBookingJobEvents } from '@/queries/use-booking-job-events.ts';
import { useNow, watchDebounced } from '@vueuse/core';

const props = defineProps<{
  bookingId: string;
}>();

const { t, locale } = useI18n();

const bookingJobs = useBookingJobs(props.bookingId);
const bookingJobEvents = useBookingJobEvents(props.bookingId);
const now = useNow({ interval: 1000 });
const visibleJobs = computed(() => bookingJobs.data.value ?? []);

function relativeRunAt(job: { status: string; runAt?: string }) {
  if (job.status !== 'delayed' || !job.runAt) {
    return;
  }
  return DateTime.fromISO(job.runAt).toRelative({
    locale: locale.value,
    base: DateTime.fromJSDate(now.value),
  });
}

watchDebounced(
  bookingJobEvents.events,
  () => {
    bookingJobs.refetch();
  },
  { debounce: 250 },
);
</script>

<i18n lang="json">
{
  "en": {
    "jobTypes": {
      "generate-contract": "Generating contract",
      "run-fraud-detection": "Running fraud detection",
      "run-schufa-evaluation": "Running SCHUFA evaluation",
      "send-handover-mail": "Sending handover email",
      "send-car-swap-mail": "Sending car swap email",
      "send-confirmation": "Sending confirmation",
      "send-created-mail": "Sending created email",
      "process-cancellation": "Processing cancellation",
      "send-cancellation-mails": "Sending cancellation emails",
      "process-returned": "Processing return",
      "send-returned-mails": "Sending return emails",
      "generate-insurance-case-invoices": "Generating insurance case invoices",
      "periodic-ledger": "Posting due rental fees",
      "periodic-invoice": "Creating period invoice",
      "periodic-charge": "Charging due period amount"
    },
    "delayReasons": {
      "invoice-pdf-not-generated": "Waiting for invoice PDF",
      "payments-not-settled": "Waiting for payment settlement",
      "contract-pdf-not-generated": "Waiting for contract PDF"
    },
    "jobSteps": {
      "generate-pdf": "Generating PDF",
      "upload-pdf": "Uploading PDF",
      "delete-related-bookings": "Deleting related bookings",
      "create-cancellation-invoice": "Creating cancellation invoice",
      "create-invoice": "Creating invoice",
      "charge-due-amount": "Charging due amount",
      "wait-for-payments": "Waiting for payments",
      "send-mail": "Sending email",
      "send-internal-mail": "Sending internal email"
    }
  },
  "de": {
    "jobTypes": {
      "generate-contract": "Vertrag wird generiert",
      "run-fraud-detection": "Betrugsprüfung wird ausgeführt",
      "run-schufa-evaluation": "SCHUFA-Prüfung wird ausgeführt",
      "send-handover-mail": "Übergabe-E-Mail wird gesendet",
      "send-car-swap-mail": "Autotausch-E-Mail wird gesendet",
      "send-confirmation": "Bestätigung wird gesendet",
      "send-created-mail": "Erstellungs-E-Mail wird gesendet",
      "process-cancellation": "Stornierung wird verarbeitet",
      "send-cancellation-mails": "Stornierungs-E-Mails werden gesendet",
      "process-returned": "Rückgabe wird verarbeitet",
      "send-returned-mails": "Rückgabe-E-Mails werden gesendet",
      "generate-insurance-case-invoices": "Versicherungs-Rechnungen werden generiert",
      "periodic-ledger": "Fällige Mietbeträge werden gebucht",
      "periodic-invoice": "Wiederkehrende Rechnung wird erstellt",
      "periodic-charge": "Fälliger Periodenbetrag wird eingezogen"
    },
    "delayReasons": {
      "invoice-pdf-not-generated": "Warten auf Rechnungs-PDF",
      "payments-not-settled": "Warten auf Zahlungsabwicklung",
      "contract-pdf-not-generated": "Warten auf Vertrags-PDF"
    },
    "jobSteps": {
      "generate-pdf": "PDF wird generiert",
      "upload-pdf": "PDF wird hochgeladen",
      "delete-related-bookings": "Verwandte Buchungen werden gelöscht",
      "create-cancellation-invoice": "Stornierungsrechnung wird erstellt",
      "create-invoice": "Rechnung wird erstellt",
      "charge-due-amount": "Fälliger Betrag wird belastet",
      "wait-for-payments": "Warten auf Zahlungen",
      "send-mail": "E-Mail wird gesendet",
      "send-internal-mail": "Interne E-Mail wird gesendet"
    }
  },
  "es": {
    "jobTypes": {
      "generate-contract": "Generando contrato",
      "run-fraud-detection": "Ejecutando detección de fraude",
      "run-schufa-evaluation": "Ejecutando evaluación SCHUFA",
      "send-handover-mail": "Enviando correo de entrega",
      "send-car-swap-mail": "Enviando correo de cambio de vehículo",
      "send-confirmation": "Enviando confirmación",
      "send-created-mail": "Enviando correo de creación",
      "process-cancellation": "Procesando cancelación",
      "send-cancellation-mails": "Enviando correos de cancelación",
      "process-returned": "Procesando devolución",
      "send-returned-mails": "Enviando correos de devolución",
      "generate-insurance-case-invoices": "Generando facturas del siniestro",
      "periodic-ledger": "Registrando cuotas de alquiler vencidas",
      "periodic-invoice": "Creando factura del periodo",
      "periodic-charge": "Cobrando el importe del periodo"
    },
    "delayReasons": {
      "invoice-pdf-not-generated": "Esperando PDF de factura",
      "payments-not-settled": "Esperando liquidación de pagos",
      "contract-pdf-not-generated": "Esperando PDF del contrato"
    },
    "jobSteps": {
      "generate-pdf": "Generando PDF",
      "upload-pdf": "Subiendo PDF",
      "delete-related-bookings": "Eliminando reservas relacionadas",
      "create-cancellation-invoice": "Creando factura de cancelación",
      "create-invoice": "Creando factura",
      "charge-due-amount": "Cobrando importe pendiente",
      "wait-for-payments": "Esperando pagos",
      "send-mail": "Enviando correo",
      "send-internal-mail": "Enviando correo interno"
    }
  }
}
</i18n>

<style scoped>
@keyframes hourglass {
  0% {
    transform: rotate(0deg);
  }
  20% {
    transform: rotate(180deg);
  }
  50% {
    transform: rotate(180deg);
  }
  70% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.animate-hourglass {
  animation: hourglass 4s ease-in-out infinite;
}
</style>
